import React, { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { debounce, filter, find, isEmpty, map } from 'lodash';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import { listCatalogProductsByDrugName } from 'store/thunks/drugThunks';
import { associateToCatalog } from 'store/thunks/workQueueThunks';

import { CATALOG_DISABLED_ASSOCIATION_NOTE } from 'common/constants/catalogStatus';
import AutoSuggest from 'common/forms/AutoSuggest';

const tableCellStye = { borderBottom: 'none', p: 1 };

// POM-5706: catalog-association alternative to ResolveDrugProfileModal, shown in the drug-review
// queue when the inventory module is enabled. The reviewer associates a catalog productId to the
// prescription instead of resolving/creating a drug profile.
const AssociateToCatalogModal = ({
  open,
  onClose,
  prescriptionId,
  lineItemId,
  drugName,
  strength,
  productId,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [hasOneTimeAssociation, setOneTimeAssociation] = useState(false);

  const handleOneTimeAssociation = () => setOneTimeAssociation(!hasOneTimeAssociation);

  // Debounced fetcher is memoized so the 500 ms timer survives re-renders (catalogLoading /
  // selectedProductId state changes) instead of being re-created on every render.
  const fetchCatalogProducts = useMemo(
    () =>
      debounce((searchText) => {
        setCatalogLoading(true);
        dispatch(listCatalogProductsByDrugName(searchText))
          .then((products) => setCatalogProducts(products || []))
          .finally(() => setCatalogLoading(false));
      }, 500),
    [dispatch]
  );

  useEffect(() => () => fetchCatalogProducts.cancel(), [fetchCatalogProducts]);

  const handleAssociateToCatalog = () => {
    setFormLoading(true);
    dispatch(
      associateToCatalog({
        prescriptionId,
        lineItemId,
        productId: selectedProductId,
        hasOneTimeAssociation,
      })
    )
      .then((response) => {
        if (response) {
          enqueueSnackbar('Catalog product associated successfully', { variant: 'success' });
          onClose();
        }
      })
      .finally(() => setFormLoading(false));
  };

  // Don't allow dismissing while an association is in flight, and ignore backdrop clicks so an
  // accidental outside-click can't discard the form. Escape and the Cancel button still close.
  // MUI passes the dismissal reason as the 2nd arg; the Cancel button's onClick leaves it undefined.
  const handleClose = (_event, reason) => {
    if (formLoading) return;
    if (reason === 'backdropClick') return;
    onClose();
  };

  // Only catalog rows that carry a productId can be associated. A product with no active NDC in
  // inventory is still listed but disabled (not selectable) — the reviewer sees it exists but
  // can't associate until an NDC is added.
  //
  // POM-6306: a catalog product disabled for ordering stays selectable. It is marked with a status
  // note, which is informational only — a disabled product is exactly what a reviewer needs to
  // resolve an order placed before the product was withdrawn.
  const options = map(
    filter(catalogProducts, (product) => !isEmpty(product.productId)),
    (product) => ({
      ...product,
      label: product.displayName,
      id: product.productId,
      disabled: !product.hasNdc,
      isCatalogDisabled: product.isCatalogEnabled === false,
    })
  );

  const selectedProduct = find(options, { id: selectedProductId });
  const isSelectedProductDisabled = !isEmpty(selectedProduct) && !selectedProduct.isCatalogEnabled;

  const isFormValid = !isEmpty(selectedProductId);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Associate to Catalog</DialogTitle>
      <Grid sx={{ px: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...tableCellStye, p: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>Drug</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DrugInfo fieldKey='Description' fieldValue={drugName} />
            <DrugInfo fieldKey='Strength' fieldValue={strength} />
            <DrugInfo fieldKey='Product ID' fieldValue={productId || 'NA'} />
          </TableBody>
        </Table>
      </Grid>

      <Form
        onSubmit={handleAssociateToCatalog}
        render={({ handleSubmit }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent sx={{ minWidth: 600 }}>
              <AutoSuggest
                id='Associate-Catalog-Product-Input'
                name='searchCatalogProduct'
                label='Search Catalog'
                loading={catalogLoading}
                options={options}
                fetchOptions={fetchCatalogProducts}
                handleSelect={setSelectedProductId}
              />

              {isSelectedProductDisabled && (
                <Alert severity='info' sx={{ mt: 2 }}>
                  {CATALOG_DISABLED_ASSOCIATION_NOTE}
                </Alert>
              )}
            </DialogContent>

            <DialogActions>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Grid container alignItems='center'>
                    <FormControlLabel
                      label='One Time Association'
                      control={
                        <Checkbox
                          checked={hasOneTimeAssociation}
                          onChange={handleOneTimeAssociation}
                        />
                      }
                      sx={{ mr: 1 }}
                    />
                    <Tooltip
                      title='This association will apply only to the current case.'
                      arrow
                      color='secondary'
                      placement='top-start'
                    >
                      <InfoIcon />
                    </Tooltip>
                  </Grid>
                </Grid>

                <Grid item>
                  <Button sx={{ mr: 1 }} variant='outlined' color='secondary' onClick={handleClose}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={formLoading}
                    disabled={!isFormValid || catalogLoading}
                    variant='contained'
                    type='submit'
                  >
                    Confirm
                  </LoadingButton>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

export default AssociateToCatalogModal;

const DrugInfo = ({ fieldKey, fieldValue }) => (
  <TableRow>
    <TableCell sx={tableCellStye}>{fieldKey}:</TableCell>
    <TableCell sx={tableCellStye}>{fieldValue} </TableCell>
  </TableRow>
);

DrugInfo.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  fieldValue: PropTypes.string.isRequired,
};

AssociateToCatalogModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  prescriptionId: PropTypes.string.isRequired,
  lineItemId: PropTypes.string.isRequired,
  drugName: PropTypes.string.isRequired,
  strength: PropTypes.string.isRequired,
  productId: PropTypes.string,
};

AssociateToCatalogModal.defaultProps = {
  productId: '',
};
